import React from 'react';
import { Box } from '@mantine/core';
import { formatField, transformDifferences } from './TimelineUtils';
import { RelationFieldChange } from 'interfaces';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { PrimitiveChange } from './PrimitiveChange';
import { ChangeItem } from './ChangeItem';
import { formatPatientName } from '@billing/validation';
interface RelationChangesProps {
  field: string;
  changeDetails: RelationFieldChange;
  green: string;
  red: string;
  status: any[];
  providers: any[];
}

export const RelationChanges: React.FC<RelationChangesProps> = ({
  field,
  changeDetails,
  green,
  red,
  status,
  providers,
}) => {
  const { before, after, differences } = changeDetails;

  // get display label for relation entities
  const getDisplayName = (item: any): string => {
    if (!item) return 'Unknown';
    switch (field) {
      case 'patient':
        return formatPatientName(item.firstName, item.lastName);
      case 'provider':
      case 'referringProviderObject':
        return `${item.firstName} ${item.lastName}`;
      case 'healthcard':
        return item.number || 'Unknown';
      case 'payor':
        return item.name || 'Unknown';
      case 'status':
        return status.find((s: any) => s.id === item.id)?.type || 'None';
      default:
        return item.id || 'Unknown';
    }
  };

  // ADDED
  if (!before && after) {
    return (
      <Box data-testid="relation-change" data-mode="added">
        <ChangeItem
          label={formatField(field)}
          before=""
          after={getDisplayName(after)}
          red={red}
          green={green}
          chip="Added"
          icon={<IconPlus size={14} />}
          iconColor="green"
        />
      </Box>
    );
  }

  // REMOVED
  if (before && !after) {
    return (
      <Box data-testid="relation-change" data-mode="removed">
        <ChangeItem
          label={formatField(field)}
          before={getDisplayName(before)}
          after="Removed"
          red={red}
          green={green}
          chip="Removed"
          icon={<IconTrash size={14} />}
          iconColor="red"
        />
      </Box>
    );
  }

  // CHANGED (reference changed)
  if (before && after && before.id !== after.id) {
    return (
      <Box data-testid="relation-change" data-mode="changed">
        <ChangeItem
          label={formatField(field)}
          before={getDisplayName(before)}
          after={getDisplayName(after)}
          red={red}
          green={green}
          chip="Changed"
          icon={<IconPencil size={14} />}
          iconColor="blue"
        />
      </Box>
    );
  }

  // MODIFIED (deep differences in same relation object)
  if (differences.length >= 0) {
    const diffObj = transformDifferences(differences);
    if (!Object.keys(diffObj).length) return <></>;
    return (
      <Box data-testid="relation-change" data-mode="modified">
        {Object.entries(diffObj).map(([path, { before, after }]) => (
          <PrimitiveChange
            key={path}
            changeDetails={{
              before,
              after,
              type: 'primitive',
              field: formatField(path),
            }}
            field={formatField(path)}
            status={status}
            providers={providers}
            red={red}
            green={green}
          />
        ))}
      </Box>
    );
  }

  return null;
};
